/**
 * Custom CEP Liquid filters shared by the send-time engine and the frontend
 * transform preview, so the preview matches what gets sent.
 */

// Date helpers are implemented inline rather than pulled from date-fns: this
// module is imported by both the backend (date-fns v2) and the frontend
// (date-fns v3), and dragging either version into the other's type-check
// program collides the two `date-fns` type identities.
const MS_PER_HOUR = 3_600_000;
const MS_PER_DAY = 86_400_000;

function isValidDate(date: Date): boolean {
  return !Number.isNaN(date.getTime());
}

const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const NAIVE_DATE_TIME_RE =
  /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2})?(\.\d+)?$/;

// Parse to a host-independent instant so the browser transform preview matches
// the UTC send engine. Date-only (YYYY-MM-DD) and offset-less date-times are
// interpreted as UTC; the platform parser would otherwise read them in the
// host's local zone (browser-local vs server-UTC). Strings carrying an explicit
// offset (or `Z`) already denote an absolute instant and pass through unchanged.
function parseDateString(value: string): Date {
  const dateOnly = DATE_ONLY_RE.exec(value);
  if (dateOnly) {
    const [, year, month, day] = dateOnly;
    return new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  }
  if (NAIVE_DATE_TIME_RE.test(value)) {
    return new Date(`${value.replace(" ", "T")}Z`);
  }
  return new Date(value);
}

// Picked-date literals fed to the `date:` filter, which parses AND formats in
// the host's local zone. Month-name (local-parsed) keeps the displayed day
// stable across hosts; an ISO date-only string would parse as UTC and shift a
// day under local formatting.
export const LIQUID_DATE_LITERAL_FORMAT = "MMMM d, yyyy";

// Comparison args for diff_days/diff_hours, which parseDateString normalizes to
// a UTC instant so the numeric diff is host-independent (browser preview ==
// send engine). ISO date-only is the branch that maps to UTC — see parseDateString.
export const LIQUID_DIFF_ARG_DATE_FORMAT = "yyyy-MM-dd";

// UTC calendar (setUTCDate, no DST) so the shift is host-independent.
function addDays(date: Date, amount: number): Date {
  const result = new Date(date.getTime());
  result.setUTCDate(result.getUTCDate() + amount);
  return result;
}

// Whole calendar days between two dates on the UTC calendar, left − right.
// UTC (not local) so the count is host-independent — see parseDateString.
function differenceInCalendarDays(left: Date, right: Date): number {
  const utcLeft = Date.UTC(
    left.getUTCFullYear(),
    left.getUTCMonth(),
    left.getUTCDate(),
  );
  const utcRight = Date.UTC(
    right.getUTCFullYear(),
    right.getUTCMonth(),
    right.getUTCDate(),
  );
  return Math.round((utcLeft - utcRight) / MS_PER_DAY);
}

// Whole hours between two dates, truncated toward zero, left − right.
function differenceInHours(left: Date, right: Date): number {
  return Math.trunc((left.getTime() - right.getTime()) / MS_PER_HOUR);
}

// Minimal structural type so this stays decoupled from which Liquid build the caller uses.
type FilterRegistrar = {
  registerFilter: (
    name: string,
    filter: (value: unknown, ...args: unknown[]) => unknown,
  ) => void;
};

// Sum of an array's numeric elements (or a numeric property of each element).
// liquidjs 10 has a built-in `sum`, but this explicit registration predates the
// v9→v10 cutover and stays: it shadows the built-in with the exact semantics
// the transform catalog's "Sum" was written against.
function sum(value: unknown, property?: unknown): number {
  if (!Array.isArray(value)) return 0;
  const key = typeof property === "string" ? property : undefined;
  let total = 0;
  for (const item of value) {
    const raw =
      key != null && item != null && typeof item === "object"
        ? (item as Record<string, unknown>)[key]
        : item;
    const parsed = Number(raw);
    if (Number.isFinite(parsed)) total += parsed;
  }
  return total;
}

/**
 * Coerce a Liquid value to a Date. Accepts Date instances, ISO-8601 strings,
 * other parseable date strings, the `date` filter's `"now"`/`"today"` literals,
 * and epoch numbers (seconds or milliseconds). Returns null when it can't be
 * parsed, so filters can pass the value through unchanged rather than emit
 * "Invalid Date".
 */
function toDate(value: unknown): Date | null {
  if (value instanceof Date) return isValidDate(value) ? value : null;
  if (typeof value === "number" && Number.isFinite(value)) {
    // Sub-1e11 values are treated as epoch seconds, larger ones as milliseconds.
    const ms = Math.abs(value) < 1e11 ? value * 1000 : value;
    const date = new Date(ms);
    return isValidDate(date) ? date : null;
  }
  if (typeof value === "string" && value.trim() !== "") {
    const trimmed = value.trim();
    // Date presets base off the literal `"now"`; the built-in `date` filter
    // resolves it, so the date-math filters must too or they no-op on presets.
    if (trimmed.toLowerCase() === "now" || trimmed.toLowerCase() === "today") {
      return new Date();
    }
    const parsed = parseDateString(trimmed);
    return isValidDate(parsed) ? parsed : null;
  }
  return null;
}

function toCount(value: unknown): number | null {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

// Returns a Date so a following `| date: "..."` formats it; passes the value
// through unchanged when it isn't a parseable date / count. `direction` shifts
// forward (+1) or backward (−1).
function shiftDays(value: unknown, days: unknown, direction: 1 | -1): unknown {
  const date = toDate(value);
  const count = toCount(days);
  return date && count != null ? addDays(date, direction * count) : value;
}

const plusDays = (value: unknown, days: unknown): unknown =>
  shiftDays(value, days, 1);

const minusDays = (value: unknown, days: unknown): unknown =>
  shiftDays(value, days, -1);

// Absolute difference between the value and the comparison date (default: now):
// the magnitude only, so the direction (before/after) doesn't flip the sign.
// Empty/omitted comparison compares against now.
function diffFrom(
  value: unknown,
  comparison: unknown,
  diff: (date: Date, other: Date) => number,
): unknown {
  const date = toDate(value);
  if (!date) return value;
  const other =
    comparison == null || comparison === "" ? new Date() : toDate(comparison);
  return other ? Math.abs(diff(date, other)) : value;
}

const diffDays = (value: unknown, comparison?: unknown): unknown =>
  diffFrom(value, comparison, differenceInCalendarDays);

const diffHours = (value: unknown, comparison?: unknown): unknown =>
  diffFrom(value, comparison, differenceInHours);

export const cepLiquidFilters: ReadonlyArray<{
  name: string;
  fn: (value: unknown, ...args: unknown[]) => unknown;
}> = [
  { name: "sum", fn: sum },
  { name: "plus_days", fn: plusDays },
  { name: "minus_days", fn: minusDays },
  { name: "diff_days", fn: diffDays },
  { name: "diff_hours", fn: diffHours },
];

export function registerCepLiquidFilters(engine: FilterRegistrar): void {
  for (const { name, fn } of cepLiquidFilters) {
    engine.registerFilter(name, fn);
  }
}
